import React, { useState, useCallback, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import styled from "styled-components";

import { getCurrentUser } from "actions/currentUserActions.js";
import { getBuyplanIdForQfill } from "actions/buyplanIdActions.js";
import { getWarehouses } from "actions/warehouseActions.js";
import { resetOrderDetails } from "actions/orderDetailsActions.js";

import { isLoadingSelector, currentUserDataSelector } from "selectors/currentUserSelector.js";
import { buyplanIdSelector } from "selectors/buyplanIdSelector.js";

import ScrollToTop from "components/ScrollToTop.jsx";
import TabsList from "components/Tabs/TabsList.jsx";

import GlobalMenuContainer from "containers/GlobalMenuContainer";
import QuickFillBannerContainer from "containers/QuickFill/QuickFillBannerContainer.jsx";
import ConfirmationDialogContainer from "containers/ConfirmationDialogContainer.jsx";
import Sidebar from "components/Sidebar/index.jsx";
import SubmitOrderBtn from "components/SubmitOrderBtn.jsx";

import CategoryFilterContainer from "containers/QuickFill/CategoryFilterContainer";
import SubmitOrderContainer from "containers/SubmitOrderContainer.jsx";

import ProductsListContainer from "containers/QuickFill/ProductsListContainer.jsx";
import BuyplanDetailsContainer from "containers/BuyplanDetailsContainer.jsx";
import BuyplanReportContainer from "containers/BuyplanReportContainer.jsx";
import { activeTabSelector } from "selectors/filtersSelector.js";

import { AGENT_ADMIN, BRANDSCOPE_ADMIN, SALES_REP, SUPER_SUPPLIER_ADMIN } from "constants.js";
import { buyplanAwaitingApprovalSelector } from "selectors/buyplanInfoSelector";
import DeclineOrderBtn from "components/DeclineOrderBtn";
import DeclineOrderDialog from "components/BuyplanReport/DeclineOrderDialog";
import i18next from "i18next";
import Loader from "components/Loader/Loader.jsx";

const getTabsConfig = (isAdminUser, qfillViewAccessRoles, forBuyplan, tabsConfigLoclisationData) => {
  let tabsConfig = {
    range: {
      id: "RANGE_PAGE_TAB",
      title: tabsConfigLoclisationData.range,
      body: <ProductsListContainer />
    },
    buyplanDetails: {
      id: "BUYPLAN_DETAILS_TAB",
      title: tabsConfigLoclisationData.buyPlan,
      body: <BuyplanDetailsContainer />
    },
    report: {
      id: "REPORT",
      title: tabsConfigLoclisationData.report,
      body: <BuyplanReportContainer />
    }
  };

  if ((isAdminUser || qfillViewAccessRoles) && !forBuyplan) {
    delete tabsConfig["buyplanDetails"];
    delete tabsConfig["report"];

    return { ...tabsConfig };
  }

  return tabsConfig;
};

const BrandLayout = ({ className }) => {
  const [isModalOpen, setModalState] = useState(false);
  const toggleModalFn = useCallback(() => {
    dispatch(resetOrderDetails())
    setModalState(!isModalOpen);
  }, [isModalOpen]);

  const dispatch = useDispatch();
  const isCurrentUserLoading = useSelector(isLoadingSelector);
  const currentUser = useSelector(currentUserDataSelector);
  const isAdminUser = currentUser && currentUser.get("role") === BRANDSCOPE_ADMIN;
  const isAgentAdmin = currentUser && currentUser.get("role") === AGENT_ADMIN;
  const isSalesRep = currentUser && currentUser.get("role") === SALES_REP;
  const isSuperSupplierAdmin = currentUser && currentUser.get("role") === SUPER_SUPPLIER_ADMIN;
  const { brandId, buyplanId } = useParams();
  const forBuyplan = buyplanId ? true : false;
  const activeTab = useSelector(activeTabSelector);
  const confirmedBuyplanId = useSelector(buyplanIdSelector);
  const isAwaitingApproval = useSelector(buyplanAwaitingApprovalSelector);
  let tabsConfigLoclisationData = {
    range: i18next.t("tabsConfig.range"),
    buyPlan: i18next.t("tabsConfig.buyPlan"),
    report: i18next.t("tabsConfig.report")
  };

  const qfillViewAccessRoles = (isSuperSupplierAdmin || isSalesRep || isAgentAdmin || isAdminUser)

  const showDeclineOrderBtn = () => {
    return (!isAdminUser && qfillViewAccessRoles && forBuyplan && isAwaitingApproval);
  };

  const [isDeclineModalOpen, setDeclineModalOpen] = useState(false);
  const toggleDeclineModel = () => {
    setDeclineModalOpen(!isDeclineModalOpen);
  };

  useEffect(() => {
    if (confirmedBuyplanId) dispatch(getCurrentUser());
  }, [dispatch, confirmedBuyplanId]);

  useEffect(() => {
    dispatch(getCurrentUser());
  }, [dispatch]);

  useEffect(() => {
    if (!currentUser) return;

    if (forBuyplan) {
      dispatch(getBuyplanIdForQfill({ brandId, buyplan_id: buyplanId }));
    } else if (!qfillViewAccessRoles) {
      dispatch(getBuyplanIdForQfill({ brandId }));
    }
  }, [dispatch, currentUser, forBuyplan, brandId, buyplanId]);

  useEffect(() => {
    if (confirmedBuyplanId) {
      dispatch(getWarehouses({ buyplanId: confirmedBuyplanId }));
    }
  }, [dispatch, confirmedBuyplanId]);

  if (isCurrentUserLoading) {
    return <Loader />; // or loader
  }

  if (!currentUser) {
    return null;
  }

  if (!qfillViewAccessRoles && !confirmedBuyplanId) {
    return null;
  }

  return (
    <>
      <GlobalMenuContainer />

      <div className={`${className}`}>
        <Sidebar />

        <main className={`main ${activeTab === "report" && "w-100"}`}>
          <QuickFillBannerContainer />

          {activeTab !== "report" && <CategoryFilterContainer />}

          <section>
            <div className="btn-wrap float-right">
              {!isAdminUser && ((forBuyplan && qfillViewAccessRoles) || (!qfillViewAccessRoles)) && (
                <SubmitOrderBtn className="btn btn-success" onClick={toggleModalFn}>
                  {showDeclineOrderBtn()
                    ? i18next.t("buttons.approveOrder")
                    : i18next.t("buttons.submitOrder")}
                </SubmitOrderBtn>
              )}

              {showDeclineOrderBtn() && (
                <DeclineOrderBtn
                  type="button"
                  className="btn btn-danger"
                  onClick={toggleDeclineModel}
                >
                  {i18next.t("buttons.declineOrder")}
                </DeclineOrderBtn>
              )}

              {!isAdminUser && ((forBuyplan && qfillViewAccessRoles) || (!qfillViewAccessRoles)) && <ConfirmationDialogContainer />}
            </div>
          </section>

          <TabsList
            tabsConfig={getTabsConfig(isAdminUser, qfillViewAccessRoles, forBuyplan, tabsConfigLoclisationData)}
          />

          {!isAdminUser && ((forBuyplan && qfillViewAccessRoles) || (!qfillViewAccessRoles)) && (
            <SubmitOrderContainer isOpen={isModalOpen} closeModalCallback={toggleModalFn} />
          )}

          {confirmedBuyplanId && showDeclineOrderBtn() && (
            <DeclineOrderDialog
              isOpen={isDeclineModalOpen}
              closeModalCallback={toggleDeclineModel}
              buyplanId={confirmedBuyplanId}
            />
          )}
        </main>

        <ScrollToTop />
      </div>
    </>
  );
};

const StyledBrandLayout = styled(BrandLayout)`
  display: flex;
  width: 100%;
  padding: 20px 15px 0;

  @media (max-width: 575.98px) {
    flex-direction: column;
  }

  .sidebar {
    width: var(--sidebar-width);
    min-width: var(--sidebar-min-width);

    @media (max-width: 575.98px) {
      width: 100%;
      margin-bottom: 8px;
    }
  }

  .main {
    width: var(--main-width);
    padding-left: 15px;

    @media (max-width: 575.98px) {
      width: 100%;
      padding-left: 0;
    }
  }

  .btn-wrap {
    padding: 11.5px 0;
    border-top: 1px solid #dddddd;
    border-bottom: 1px solid #dddddd;
    min-height: 55px;
    display: flex;

    @media (max-width: 575.98px) {
      float: none !important;
      margin-bottom: -1px;
      text-align: right;
    }
  }

  .brand-banner {
    margin: 0 0 10px;
  }
`;

export default React.memo(StyledBrandLayout);
